<template>
  <header ref="root" class="page-hero">
    <div class="page-hero-overlay" />
    <div class="page-hero-inner">
      <div ref="bodyRef" class="page-hero-body">
        <div class="section">
          <div class="container">
            <div class="page-hero-title text-balance">
              <h1 v-if="title" v-blokkli-editable:title class="title is-h1">
                {{ title }}
              </h1>
              <div
                v-if="subtitle"
                v-blokkli-editable:field_lead
                class="subtitle is-h3"
              >
                {{ subtitle }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="media"
        ref="mediaRef"
        v-blokkli-droppable:field_keyvisual_media
        class="page-hero-media"
      >
        <MediaImage
          v-if="'image' in media || 'assetId' in media"
          v-bind="media"
          responsive-style="page_hero"
          loading="eager"
        />
        <MediaVideo
          v-if="'__typename' in media && media.__typename === 'MediaVideo'"
          ref="videoRef"
          v-bind="media"
        />
        <MediaVideoCanto
          v-if="'__typename' in media && media.__typename === 'MediaCantoVideo'"
          v-bind="media"
        />
      </div>
    </div>
  </header>
</template>

<script lang="ts" setup>
import type {
  MediaImageFragment,
  MediaCantoFragment,
  MediaVideoFragment,
  MediaVideoCantoFragment,
} from '#graphql-operations'
import { useElementVisibility, useRafFn } from '@vueuse/core'

defineProps<{
  title: string | undefined
  subtitle: string | undefined
  media:
    | MediaImageFragment
    | MediaCantoFragment
    | MediaVideoFragment
    | MediaVideoCantoFragment
    | object
}>()

const root = ref<HTMLElement>()
const bodyRef = ref<HTMLElement>()
const mediaRef = ref<HTMLElement>()
const videoRef = ref<HTMLVideoElement>()

const isVisible = useElementVisibility(root)

watch(isVisible, function (isVisibleNow) {
  if (!videoRef.value) {
    return
  }
  if (isVisibleNow) {
    videoRef.value.play()
  } else {
    videoRef.value.pause()
  }
})

const loop = () => {
  if (!root.value || !isVisible.value) return

  const height = root.value.clientHeight
  const visibleHeight = height + 250
  const scrollY = window.scrollY

  if (isVisible.value && bodyRef.value && mediaRef.value) {
    bodyRef.value.style.transform = `translateY(${scrollY / 2}px)`
    const ratio = Math.min(Math.max(scrollY / (visibleHeight / 3) - 0.7, 0), 1)
    bodyRef.value.style.opacity = (1 - ratio).toString()
    mediaRef.value.style.transform = `translateY(${scrollY / 4}px)`
  }
}

useRafFn(loop)
</script>

<style>
@reference '~/assets/css/tailwind.css';
.page-hero {
  @apply relative;
  margin-top: -10rem;
  @variant tablet {
    margin-top: calc(var(--breadcrumb-height) * -1);
  }
  &:before {
    content: '';
  }
  .title,
  .subtitle {
    @apply text-white;
    text-shadow:
      0 0 0.6em rgba(0, 0, 0, 0.3),
      0 0 0.3em rgba(0, 0, 0, 0.4);
  }

  figure,
  picture,
  .contextual-region {
    @apply absolute top-0 left-0 z-0 h-full w-full;
  }

  img,
  video {
    @apply h-full w-full object-cover;
  }

  figure > div,
  figure > div > div {
    @apply h-full;
    .image {
      @apply h-full;
    }
  }
}

.page-hero-overlay {
  @apply pointer-events-none relative top-0 left-0 h-[20rem] w-full desktop:sticky;
  z-index: 9;
  background: linear-gradient(to top, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.8));
}

.page-hero,
.page-hero-inner {
  @apply h-screen;
  min-height: initial;
  max-height: 1024px;
  @variant desktop {
    max-height: 768px;
  }
  @variant widescreen {
    max-height: 880px;
  }
  @variant fullhd {
    max-height: 1440px;
  }
  + .page-content {
    z-index: 10;
  }
}

.page-hero-inner {
  @apply overflow-hidden;
}

.page-hero-body {
  @apply relative;
  z-index: 3;
  will-change: transform, opacity;
}

.page-hero-title {
  max-width: 50rem;
}

.page-hero-media {
  @apply absolute top-0 left-0 h-full w-full will-change-transform;
  z-index: 2;
}

.page-hero-inner {
  @apply absolute top-0 left-0 flex w-full flex-col justify-center bg-scheme-main-bis;
  z-index: 1;
}
</style>
